import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Delivery,
  DeliveryStatus,
  DELIVERY_STATUS_CONFIG,
} from '../types';
import { deliveryService } from '../services/deliveryService';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

// ============================================================================
// WALLBOARD - Monitor tipo aeropuerto para recepción de mercadería
// ============================================================================
// Dark theme, fuente grande, optimizado para TV 1920x1080 y 4K
// Sin sidebar, sin header — pantalla completa
// Auto-refresh cada 30 segundos
// ============================================================================

const REFRESH_INTERVAL = 30_000; // 30 seconds
const ROWS_PER_PAGE = 8; // Rows visible at once
const PAGE_TRANSITION_MS = 8_000; // Time per page

// ============================================================================
// Wallboard Colors & Styles
// ============================================================================

const WB = {
  bg: '#0a0e1a',
  bgAlt: '#111827',
  bgRow: 'rgba(255, 255, 255, 0.03)',
  bgRowAlt: 'rgba(255, 255, 255, 0.06)',
  bgHeader: 'rgba(0, 45, 98, 0.6)',
  text: '#e2e8f0',
  textDim: '#94a3b8',
  textBright: '#f8fafc',
  accent: '#60a5fa',
  border: 'rgba(255, 255, 255, 0.08)',
  headerGradient: 'linear-gradient(135deg, #001d42 0%, #002d62 50%, #1a4d8f 100%)',
  footerBg: 'rgba(0, 0, 0, 0.5)',
  // Interbarge brand
  brandRed: '#b32317',
  brandBlue: '#002d62',
} as const;

// ============================================================================
// Status Badge Component
// ============================================================================

const StatusBadge: React.FC<{ status: DeliveryStatus; isDelayed?: boolean }> = ({ status, isDelayed }) => {
  const cfg = DELIVERY_STATUS_CONFIG[status];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        px: '16px',
        py: '8px',
        borderRadius: '6px',
        backgroundColor: cfg.wallboardBg,
        border: `1px solid ${cfg.wallboardColor}40`,
        animation: isDelayed ? 'pulse-delayed 2s ease-in-out infinite' : undefined,
        '@keyframes pulse-delayed': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.6 },
        },
      }}
    >
      <Box
        sx={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: cfg.wallboardColor,
          boxShadow: `0 0 8px ${cfg.wallboardColor}80`,
          animation: isDelayed ? 'blink-dot 1.5s ease-in-out infinite' : undefined,
          '@keyframes blink-dot': {
            '0%, 100%': { opacity: 1, boxShadow: `0 0 8px ${cfg.wallboardColor}80` },
            '50%': { opacity: 0.3, boxShadow: `0 0 16px ${cfg.wallboardColor}` },
          },
        }}
      />
      <Typography
        sx={{
          color: cfg.wallboardColor,
          fontWeight: 700,
          fontSize: '18px',
          letterSpacing: '1px',
          fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", monospace',
          textTransform: 'uppercase',
        }}
      >
        {cfg.label}
      </Typography>
    </Box>
  );
};

// ============================================================================
// Live Clock Component
// ============================================================================

const LiveClock: React.FC = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box sx={{ textAlign: 'right' }}>
      <Typography
        sx={{
          color: WB.textBright,
          fontSize: '48px',
          fontWeight: 700,
          fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", monospace',
          letterSpacing: '3px',
          lineHeight: 1,
        }}
      >
        {format(time, 'HH:mm:ss')}
      </Typography>
      <Typography
        sx={{
          color: WB.textDim,
          fontSize: '18px',
          fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", monospace',
          mt: '4px',
        }}
      >
        {format(time, "EEEE d 'de' MMMM, yyyy", { locale: es })}
      </Typography>
    </Box>
  );
};

// ============================================================================
// Row Flash Animation on State Change
// ============================================================================

const FlashRow: React.FC<{
  children: React.ReactNode;
  isEven: boolean;
  status: DeliveryStatus;
}> = ({ children, isEven, status }) => {
  const [flash, setFlash] = useState(false);
  const prevStatusRef = useRef(status);

  useEffect(() => {
    if (prevStatusRef.current !== status) {
      setFlash(true);
      prevStatusRef.current = status;
      const timer = setTimeout(() => setFlash(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const cfg = DELIVERY_STATUS_CONFIG[status];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '2.5fr 3fr 1.2fr 1.3fr 2fr',
        alignItems: 'center',
        px: '24px',
        py: '14px',
        backgroundColor: flash
          ? `${cfg.wallboardColor}25`
          : isEven
            ? WB.bgRow
            : WB.bgRowAlt,
        borderBottom: `1px solid ${WB.border}`,
        transition: 'background-color 0.8s ease',
        animation: flash ? 'row-flash 2s ease-out' : undefined,
        textDecoration: status === 'cancelled' ? 'line-through' : 'none',
        opacity: status === 'cancelled' ? 0.4 : status === 'received' ? 0.6 : 1,
        '@keyframes row-flash': {
          '0%': { backgroundColor: `${cfg.wallboardColor}40` },
          '100%': { backgroundColor: isEven ? WB.bgRow : WB.bgRowAlt },
        },
      }}
    >
      {children}
    </Box>
  );
};

// ============================================================================
// Ticker / Marquee Component
// ============================================================================

const Ticker: React.FC<{ items: string[] }> = ({ items }) => {
  const text = items.join('     •     ');
  return (
    <Box
      sx={{
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        flex: 1,
        ml: 3,
      }}
    >
      <Box
        sx={{
          display: 'inline-block',
          animation: `ticker ${Math.max(20, items.length * 5)}s linear infinite`,
          '@keyframes ticker': {
            '0%': { transform: 'translateX(100%)' },
            '100%': { transform: 'translateX(-100%)' },
          },
        }}
      >
        <Typography
          sx={{
            color: WB.accent,
            fontSize: '16px',
            fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", monospace',
            letterSpacing: '0.5px',
          }}
        >
          {text}
        </Typography>
      </Box>
    </Box>
  );
};

// ============================================================================
// Main Wallboard Component
// ============================================================================

const Wallboard: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const locationName = decodeURIComponent(locationId || '');

  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [showCloseButton, setShowCloseButton] = useState(false);

  // Load deliveries
  const loadDeliveries = useCallback(() => {
    const all = deliveryService.getActiveByLocation(locationName);
    // Sort by priority (delayed first, then at_port, etc.)
    const sorted = all.sort((a, b) => {
      const priorityA = DELIVERY_STATUS_CONFIG[a.status].priority;
      const priorityB = DELIVERY_STATUS_CONFIG[b.status].priority;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return new Date(a.estimatedArrival).getTime() - new Date(b.estimatedArrival).getTime();
    });
    setDeliveries(sorted);
    setLastUpdate(new Date());
  }, [locationName]);

  // Initial load + auto-refresh
  useEffect(() => {
    loadDeliveries();
    const interval = setInterval(loadDeliveries, REFRESH_INTERVAL);
    const handler = () => loadDeliveries();
    window.addEventListener('deliveriesUpdated', handler);
    return () => {
      clearInterval(interval);
      window.removeEventListener('deliveriesUpdated', handler);
    };
  }, [loadDeliveries]);

  // Auto-pagination
  const totalPages = Math.max(1, Math.ceil(deliveries.length / ROWS_PER_PAGE));

  useEffect(() => {
    if (totalPages <= 1) return;
    const interval = setInterval(() => {
      setCurrentPage(prev => (prev + 1) % totalPages);
    }, PAGE_TRANSITION_MS);
    return () => clearInterval(interval);
  }, [totalPages]);

  // ESC key to exit
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') navigate('/reception');
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [navigate]);

  // Current page of deliveries
  const startIdx = currentPage * ROWS_PER_PAGE;
  const visibleDeliveries = deliveries.slice(startIdx, startIdx + ROWS_PER_PAGE);

  // Stats for footer
  const pending = deliveries.filter(d => !['received', 'cancelled'].includes(d.status)).length;
  const atPort = deliveries.filter(d => d.status === 'at_port').length;
  const delayed = deliveries.filter(d => d.status === 'delayed').length;

  // Ticker items - upcoming deliveries
  const tickerItems = deliveries
    .filter(d => ['in_transit', 'scheduled'].includes(d.status))
    .map(d => {
      const eta = format(parseISO(d.estimatedArrival), 'HH:mm', { locale: es });
      return `${d.supplierName} → ETA ${eta}`;
    });

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: WB.bg,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: '"Inter", "Noto Sans", sans-serif',
        cursor: 'none',
        '&:hover': { cursor: 'default' },
      }}
      onMouseMove={() => {
        setShowCloseButton(true);
        clearTimeout((window as any).__wallboardHideTimer);
        (window as any).__wallboardHideTimer = setTimeout(() => setShowCloseButton(false), 3000);
      }}
    >
      {/* Load Google Fonts for monospace */}
      <link
        href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      {/* ================================================================= */}
      {/* HEADER */}
      {/* ================================================================= */}
      <Box
        sx={{
          background: WB.headerGradient,
          px: 4,
          py: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `2px solid ${WB.brandRed}`,
          flexShrink: 0,
        }}
      >
        {/* Left: Brand */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box>
            <Typography
              sx={{
                color: WB.textBright,
                fontSize: '16px',
                fontWeight: 500,
                letterSpacing: '3px',
                textTransform: 'uppercase',
                opacity: 0.8,
              }}
            >
              INTERBARGE
            </Typography>
            <Typography
              sx={{
                color: WB.textBright,
                fontSize: '36px',
                fontWeight: 700,
                letterSpacing: '1px',
                lineHeight: 1.1,
                mt: '2px',
              }}
            >
              {locationName || 'TODAS LAS UBICACIONES'}
            </Typography>
          </Box>
        </Box>

        {/* Center: Reception Board label */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              color: WB.textBright,
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '6px',
              textTransform: 'uppercase',
              opacity: 0.6,
            }}
          >
            RECEPCIÓN DE MERCADERÍA
          </Typography>
        </Box>

        {/* Right: Clock */}
        <LiveClock />
      </Box>

      {/* ================================================================= */}
      {/* TABLE HEADER */}
      {/* ================================================================= */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '2.5fr 3fr 1.2fr 1.3fr 2fr',
          px: '24px',
          py: '12px',
          backgroundColor: 'rgba(0, 45, 98, 0.3)',
          borderBottom: `2px solid ${WB.border}`,
          flexShrink: 0,
        }}
      >
        {['PROVEEDOR', 'MERCADERÍA', 'CANTIDAD', 'ETA', 'ESTADO'].map(header => (
          <Typography
            key={header}
            sx={{
              color: WB.accent,
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '3px',
              fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", monospace',
              textTransform: 'uppercase',
            }}
          >
            {header}
          </Typography>
        ))}
      </Box>

      {/* ================================================================= */}
      {/* TABLE BODY */}
      {/* ================================================================= */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'opacity 0.5s ease',
        }}
      >
        {visibleDeliveries.length > 0 ? (
          visibleDeliveries.map((delivery, index) => {
            const cfg = DELIVERY_STATUS_CONFIG[delivery.status];
            const isDelayed = delivery.status === 'delayed';
            const itemsSummary = delivery.items.length === 1
              ? delivery.items[0].description
              : delivery.items.map(i => i.description).join(', ');
            const quantitySummary = delivery.items.length === 1
              ? `${delivery.items[0].quantity} ${delivery.items[0].unit}`
              : `${delivery.items.length} items`;

            return (
              <FlashRow
                key={delivery.id}
                isEven={index % 2 === 0}
                status={delivery.status}
              >
                {/* Supplier */}
                <Typography
                  sx={{
                    color: WB.textBright,
                    fontSize: '22px',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    pr: 2,
                  }}
                >
                  {delivery.supplierName}
                </Typography>

                {/* Items */}
                <Typography
                  sx={{
                    color: WB.text,
                    fontSize: '20px',
                    fontWeight: 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    pr: 2,
                  }}
                >
                  {itemsSummary}
                </Typography>

                {/* Quantity */}
                <Typography
                  sx={{
                    color: WB.text,
                    fontSize: '20px',
                    fontWeight: 500,
                    fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", monospace',
                  }}
                >
                  {quantitySummary}
                </Typography>

                {/* ETA */}
                <Typography
                  sx={{
                    color: isDelayed ? cfg.wallboardColor : WB.text,
                    fontSize: '22px',
                    fontWeight: 600,
                    fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", monospace',
                    animation: isDelayed ? 'pulse-delayed 2s ease-in-out infinite' : undefined,
                  }}
                >
                  {format(parseISO(delivery.estimatedArrival), 'HH:mm')}
                </Typography>

                {/* Status */}
                <StatusBadge status={delivery.status} isDelayed={isDelayed} />
              </FlashRow>
            );
          })
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                color: WB.textDim,
                fontSize: '28px',
                fontWeight: 500,
                letterSpacing: '2px',
              }}
            >
              SIN ENTREGAS PROGRAMADAS
            </Typography>
          </Box>
        )}

        {/* Fill remaining space with empty rows for consistent look */}
        {visibleDeliveries.length > 0 && visibleDeliveries.length < ROWS_PER_PAGE && (
          Array.from({ length: ROWS_PER_PAGE - visibleDeliveries.length }).map((_, i) => (
            <Box
              key={`empty-${i}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: '2.5fr 3fr 1.2fr 1.3fr 2fr',
                px: '24px',
                py: '14px',
                backgroundColor: (visibleDeliveries.length + i) % 2 === 0 ? WB.bgRow : WB.bgRowAlt,
                borderBottom: `1px solid ${WB.border}`,
              }}
            >
              {/* Empty row */}
            </Box>
          ))
        )}
      </Box>

      {/* ================================================================= */}
      {/* FOOTER */}
      {/* ================================================================= */}
      <Box
        sx={{
          backgroundColor: WB.footerBg,
          borderTop: `2px solid ${WB.brandRed}`,
          px: 4,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        {/* Left: Summary stats */}
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#60a5fa' }} />
            <Typography sx={{ color: WB.text, fontSize: '15px', fontFamily: '"JetBrains Mono", monospace' }}>
              {pending} pendientes
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#00e676' }} />
            <Typography sx={{ color: WB.text, fontSize: '15px', fontFamily: '"JetBrains Mono", monospace' }}>
              {atPort} en puerto
            </Typography>
          </Box>
          {delayed > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: '#ff1744',
                  animation: 'blink-dot 1.5s ease-in-out infinite',
                  '@keyframes blink-dot': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.3 },
                  },
                }}
              />
              <Typography
                sx={{
                  color: '#ff1744',
                  fontSize: '15px',
                  fontWeight: 700,
                  fontFamily: '"JetBrains Mono", monospace',
                }}
              >
                {delayed} demoradas
              </Typography>
            </Box>
          )}
        </Box>

        {/* Center: Ticker */}
        {tickerItems.length > 0 && <Ticker items={tickerItems} />}

        {/* Right: Pagination & Update time */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {Array.from({ length: totalPages }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: i === currentPage ? 20 : 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: i === currentPage ? WB.accent : WB.textDim,
                    transition: 'all 0.3s ease',
                    opacity: i === currentPage ? 1 : 0.3,
                  }}
                />
              ))}
            </Box>
          )}
          <Typography
            sx={{
              color: WB.textDim,
              fontSize: '13px',
              fontFamily: '"JetBrains Mono", monospace',
              whiteSpace: 'nowrap',
            }}
          >
            Actualizado: {format(lastUpdate, 'HH:mm:ss')}
          </Typography>
        </Box>
      </Box>

      {/* ================================================================= */}
      {/* Close Button (appears on mouse move) */}
      {/* ================================================================= */}
      <Tooltip title="Salir del Wallboard (ESC)" placement="left">
        <IconButton
          onClick={() => navigate('/reception')}
          sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            color: WB.textDim,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            opacity: showCloseButton ? 0.8 : 0,
            transition: 'opacity 0.3s ease',
            '&:hover': {
              opacity: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
            zIndex: 1000,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default Wallboard;
